// /clients/log-st-bonus — Modul 5: manualni zapis spojenych treninku (ST) a bonusovych treninku.
// Owner/manager UI doplnek k cron `bonusoveTreninky` (ten zpracovava jen Reservio bookings).
//
// Features:
//   - KPI klikaci dlazdice (filter typu / zadavatele)
//   - Tab + search + filter mesic + filter zadavatel
//   - Modal Pridat ST/Bonus s fuzzy autocomplete klientu (jmeno NEBO prijmeni match)
//   - Confirm dialog pred Pridat / Smazat
//   - Owner/manager muze smazat zaznam (DELETE backend)

import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  History, Plus, RefreshCw, AlertTriangle, ArrowLeft, Search, Trash2,
  Activity, Award, Bot, User as UserIcon, CheckCircle2,
} from "lucide-react";
import Card from "../components/Card.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import InfoTip from "../components/InfoTip.jsx";
import ClientAutocomplete, { CLIENT_AUTOCOMPLETE_TOOLTIP, normalizeLoose } from "../components/ClientAutocomplete.jsx";
import { Stranka, Modal, Field, DlazdiceFiltr, Stitek, EmptyState, SkeletonRows, Hlaska, AkceZnovu } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { useClientsList } from "../lib/clientsCache.js";
import { useApiData, apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { formatDate as fmtDate } from "../lib/format.js";
import { toast } from "../components/Toast.jsx";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return fmtDate(d);
}
// Mapuje raw "Zadavatel" hodnoty na UI display.
// "backfill" / "automaticky" / "automatizace" → "Automatizace" (vsechny auto crony).
// Cokoliv jine → ponechano (jmeno cloveka).
function displayZadavatel(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (!v) return "—";
  if (["automatizace", "automaticky", "backfill"].includes(v)) return "Automatizace";
  return raw.trim();
}
function isAutoZadavatel(raw) {
  const v = String(raw || "").trim().toLowerCase();
  return ["automatizace", "automaticky", "backfill"].includes(v);
}

// normalizeLoose importujeme ze sdileneho ClientAutocomplete komponentu

// ClientAutocomplete je teď ze sdíleného src/components/ClientAutocomplete.jsx

// Potvrzovací okno ze vzorníku. Na telefonu vyjede zespodu a patka se odlepí
// od home indikátoru (dřív seděla tlačítka přesně na čárce pro zavření aplikace).
function ConfirmDialog({ title, message, confirmLabel, cancelLabel, variant, onConfirm, onCancel }) {
  const nebezpecne = variant === "danger";
  const Ikona = nebezpecne ? AlertTriangle : CheckCircle2;
  return (
    <Modal
      open onClose={onCancel} title={title} size="sm"
      footer={
        <>
          <button type="button" onClick={onCancel} className="btn btn-secondary">{cancelLabel || "Zrušit"}</button>
          <button type="button" onClick={onConfirm} className={nebezpecne ? "btn btn-danger" : "btn btn-primary"}>{confirmLabel || "Potvrdit"}</button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${nebezpecne ? "bg-rose-100 text-rose-600" : "bg-brand-100 text-brand-deep"}`}>
          <Ikona className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 text-sm text-slate-600">{message}</div>
      </div>
    </Modal>
  );
}

// Modal pro pridani ST nebo Bonus
function AddModal({ type, onClose, onSubmit, submitting, error, klientOptions, klientTrenerMap }) {
  const [klient, setKlient] = useState("");
  const [datum, setDatum] = useState(new Date().toISOString().substring(0, 10));
  const [pocet, setPocet] = useState(1);
  const [trener, setTrener] = useState("");
  const [trenerAutoFilled, setTrenerAutoFilled] = useState(false); // pokud user prepise rucne, dale nemenime
  const [duvod, setDuvod] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [confirmShown, setConfirmShown] = useState(false);

  // Autofill trener z profilu klienta. Spousti se kdykoli se zmeni klient field
  // a klient match v map. User muze trenera prepsat — pak uz autofill nelez (TODO).
  useEffect(() => {
    if (!klient || !klientTrenerMap) return;
    const matchTrener = klientTrenerMap[klient.trim()];
    if (matchTrener && (!trener || trenerAutoFilled)) {
      setTrener(matchTrener);
      setTrenerAutoFilled(true);
    }
  }, [klient, klientTrenerMap]);

  const canSubmit = klient.trim().length > 0 && datum && pocet > 0;
  const nazev = type === "ST" ? "spojený trénink (ST)" : "bonusový trénink";

  const buildPayload = () => type === "ST"
    ? { action: "add-st", klient, datum, pocet, trener, duvod }
    : { action: "add-bonus", klient, datum, pocet, duvod, poznamka };

  return (
    <>
      <Modal
        open onClose={onClose} title={`Přidat ${nazev}`} size="sm"
        footer={
          <>
            <button type="button" onClick={onClose} className="btn btn-secondary">Zrušit</button>
            <button type="button" onClick={() => setConfirmShown(true)} disabled={submitting || !canSubmit} className="btn btn-primary">
              {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {submitting ? "Ukládám…" : `Přidat ${type}`}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <div className="label flex items-center gap-1.5">Klient <InfoTip text={CLIENT_AUTOCOMPLETE_TOOLTIP} /></div>
            <ClientAutocomplete value={klient} onChange={setKlient} options={klientOptions} accentColor="violet" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Datum">
              <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} required className="field" />
            </Field>
            <Field label="Počet">
              <input type="number" min="1" max="20" value={pocet}
                onChange={(e) => setPocet(parseInt(e.target.value, 10) || 1)} className="field" />
            </Field>
          </div>

          {/* Trenér se vyplní automaticky z profilu klienta (client.trener), pole nezobrazujeme.
              Pokud klient trenéra v profilu nemá, zůstane prázdný — backend i Sheet to akceptuje. */}

          <Field label={type === "ST" ? "Důvod" : "Důvod bonusu"}>
            <input value={duvod} onChange={(e) => setDuvod(e.target.value)}
              placeholder={type === "ST" ? "Např. Spojený trénink mimo Reservio" : "Např. 10 ST splněno mimo standard"}
              className="field" />
          </Field>

          {type === "Bonus" && (
            <Field label="Poznámka" hint="Nepovinné.">
              <textarea value={poznamka} onChange={(e) => setPoznamka(e.target.value)} rows={2} placeholder="Volitelně, kontext"
                className="field resize-none" />
            </Field>
          )}

          {error && <Hlaska ton="problem" title="Uložení se nepovedlo">{error}</Hlaska>}

          <Hlaska ton="pozor" title="Audit log">
            Záznam se uloží s tvým jménem jako Zadavatel. Cron <code className="font-mono">bonusoveTreninky</code> ho započítá při příštím běhu.
          </Hlaska>
        </div>
      </Modal>

      {confirmShown && (
        <ConfirmDialog
          title={`Přidat ${type === "ST" ? "spojený trénink" : "bonusový trénink"}?`}
          message={
            <div className="space-y-1">
              <div><strong className="text-slate-900">{klient}</strong></div>
              <div className="text-xs text-slate-500">{formatDate(datum)}, {pocet}× {type}{duvod ? `, ${duvod}` : ""}</div>
            </div>
          }
          confirmLabel="Ano, přidat"
          cancelLabel="Zpět"
          onConfirm={() => { setConfirmShown(false); onSubmit(buildPayload()); }}
          onCancel={() => setConfirmShown(false)}
        />
      )}
    </>
  );
}

export default function LogStBonus() {
  const navigate = useNavigate();
  const uzka = useUzkaObrazovka();
  const { data, setData, loading, error, refetch, refreshing } = useApiData("/api/log-st-bonus");
  const currentUser = getCurrentUser();
  const canDelete = currentUser && ["owner", "manager"].includes(currentUser.role);

  // URL search params — pre-fill filter pri prokliku z ClientDetail.
  // Format: /clients/log-st-bonus?klient=Jméno+Příjmení&tab=bonus
  const location = useLocation();
  const _urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const _urlKlient = _urlParams.get("klient") || "";
  const _urlTab = _urlParams.get("tab") || "";

  const [tab, setTab] = useState(_urlTab === "bonus" || _urlTab === "st" ? _urlTab : "all");
  // Filter zadavatele — "all" / konkretni jmeno / "auto" (vsechny automaty)
  const [filterZadavatel, setFilterZadavatel] = useState("all");
  const _now = new Date();
  // Pri prokliku s ?klient= chceme videt VSECHNY zaznamy daneho klienta -> "all"
  const [filterMonth, setFilterMonth] = useState(_urlKlient ? "all" : String(_now.getMonth() + 1));
  const [filterYear, setFilterYear] = useState(_urlKlient ? "all" : String(_now.getFullYear()));
  const [search, setSearch] = useState(_urlKlient || "");
  const [modal, setModal] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Klient seznam — instant z sessionStorage cache (sdileny hook s retry)
  const klientOptions = useClientsList();
  const [klientTrenerMap, setKlientTrenerMap] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Mapa klient -> trener pro autofill v AddModal (jen ST typ). Fetch zvlast,
  // autocomplete funguje i bez tohoto.
  useEffect(() => {
    apiFetch("/api/clients").then((r) => r.json()).then((j) => {
      const arr = Array.isArray(j) ? j : (j.clients || []);
      const map = {};
      arr.forEach((c) => {
        if (c.klient && c.trener) map[String(c.klient).trim()] = String(c.trener).trim();
      });
      setKlientTrenerMap(map);
    }).catch(() => {});
  }, []);

  const stats = data?.stats || {};
  const stEntries = data?.st || [];
  const bonusEntries = data?.bonus || [];

  const allEntries = useMemo(() => {
    const arr = [];
    stEntries.forEach((e) => arr.push({ ...e, type: "ST" }));
    bonusEntries.forEach((e) => arr.push({ ...e, type: "Bonus" }));
    return arr;
  }, [stEntries, bonusEntries]);

  // Parser datumu — zvladne oba formaty: YYYY-MM-DD a D.M.YYYY (Sheet ulkadu jako D.M.YYYY).
  // Vraci { year, month } nebo null.
  function parseDateMY(raw) {
    const s = String(raw || "").trim();
    if (!s) return null;
    // YYYY-MM-DD
    const iso = s.match(/^(\d{4})-(\d{1,2})-/);
    if (iso) return { year: iso[1], month: String(parseInt(iso[2], 10)) };
    // D.M.YYYY
    const cz = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (cz) return { year: cz[3], month: String(parseInt(cz[2], 10)) };
    return null;
  }

  // Unique zadavatele (krome auto) pro dropdown
  const zadavateleManual = useMemo(() => {
    const set = new Set();
    allEntries.forEach((e) => {
      const z = String(e.zadavatel || "").trim();
      if (z && !isAutoZadavatel(z)) set.add(z);
    });
    return [...set].sort();
  }, [allEntries]);

  // Roky pro filter — z dat, sort desc
  const roky = useMemo(() => {
    const set = new Set();
    allEntries.forEach((e) => {
      const p = parseDateMY(e.datum);
      if (p) set.add(p.year);
    });
    return [...set].sort().reverse();
  }, [allEntries]);

  const filtered = useMemo(() => {
    return allEntries.filter((e) => {
      if (tab === "st" && e.type !== "ST") return false;
      if (tab === "bonus" && e.type !== "Bonus") return false;
      const isAuto = isAutoZadavatel(e.zadavatel);
      if (filterZadavatel === "auto" && !isAuto) return false;
      if (filterZadavatel === "manual" && isAuto) return false;
      if (filterZadavatel !== "all" && filterZadavatel !== "auto" && filterZadavatel !== "manual") {
        // Konkretni jmeno
        if (String(e.zadavatel || "").trim() !== filterZadavatel) return false;
      }
      // Mesicni + rocni filter
      if (filterMonth !== "all" || filterYear !== "all") {
        const p = parseDateMY(e.datum);
        if (!p) return false;
        if (filterMonth !== "all" && p.month !== filterMonth) return false;
        if (filterYear !== "all" && p.year !== filterYear) return false;
      }
      if (search) {
        const sNorm = normalizeLoose(search);
        const tokens = sNorm.split(" ").filter(Boolean);
        const hay = normalizeLoose(`${e.klient} ${e.zadavatel} ${e.duvod} ${e.poznamka || ""}`);
        if (!tokens.every((t) => hay.includes(t))) return false;
      }
      return true;
    }).sort((a, b) => {
      // Datum descending — parse oba formaty
      const pa = parseDateMY(a.datum);
      const pb = parseDateMY(b.datum);
      if (!pa && !pb) return 0;
      if (!pa) return 1;
      if (!pb) return -1;
      // Compare YYYY-MM-DD prevedeno na sortable
      const aIso = a.datum.match(/^\d{4}-/) ? a.datum.substring(0, 10)
        : (() => { const m = a.datum.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/); return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : ""; })();
      const bIso = b.datum.match(/^\d{4}-/) ? b.datum.substring(0, 10)
        : (() => { const m = b.datum.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/); return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : ""; })();
      return bIso.localeCompare(aIso);
    });
  }, [allEntries, tab, filterZadavatel, filterMonth, filterYear, search]);

  // Optimistic update — pridame zaznam okamzite do UI, modal zavreme. Backend volani na pozadi.
  // Pri selhani: rollback a chyba.
  const handleAdd = async (payload) => {
    setSubmitError(null);
    setModal(null); // zavreme modal okamzite
    const tmpId = `tmp_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const optimistic = {
      _tmp: tmpId,
      type: payload.action === "add-st" ? "ST" : "Bonus",
      rowNum: 99999, // bude prepsano po refetch
      klient: payload.klient,
      datum: payload.datum,
      pocet: payload.pocet || 1,
      zadavatel: currentUser?.name || currentUser?.email || "—",
      duvod: payload.duvod || "",
      poznamka: payload.poznamka || "",
      trener: payload.trener || "",
    };
    // Pridame do data state okamzite
    setData((prev) => {
      if (!prev) return prev;
      if (payload.action === "add-st") {
        return { ...prev, st: [...(prev.st || []), optimistic], stats: { ...prev.stats, totalST: (prev.stats?.totalST || 0) + (payload.pocet || 1), manualSTCount: (prev.stats?.manualSTCount || 0) + 1 } };
      } else {
        return { ...prev, bonus: [...(prev.bonus || []), optimistic], stats: { ...prev.stats, totalBonus: (prev.stats?.totalBonus || 0) + (payload.pocet || 1), manualBonusCount: (prev.stats?.manualBonusCount || 0) + 1 } };
      }
    });
    // Backend volani na pozadi
    try {
      const res = await apiFetch("/api/log-st-bonus", { method: "POST", body: JSON.stringify(payload) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      // Refetch fresh data (nahradi optimistic real radkem s rowNum z backendu)
      refetch();
    } catch (e) {
      setSubmitError(e.message);
      toast(`Chyba uložení: ${e.message}`, "error");
      // Rollback
      refetch();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null); // zavreme dialog okamzite
    // Optimistic remove ze state
    setData((prev) => {
      if (!prev) return prev;
      if (target.type === "ST") {
        return { ...prev, st: (prev.st || []).filter((e) => e.rowNum !== target.rowNum) };
      } else {
        return { ...prev, bonus: (prev.bonus || []).filter((e) => e.rowNum !== target.rowNum) };
      }
    });
    try {
      const res = await apiFetch("/api/log-st-bonus", {
        method: "POST",
        body: JSON.stringify({
          action: "delete",
          type: target.type.toUpperCase(),
          rowNum: target.rowNum,
          klient: target.klient,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      refetch(); // sync s backendem (rowNum se posunul)
    } catch (e) {
      toast(`Chyba mazání: ${e.message}`, "error");
      refetch(); // rollback
    }
  };

  const MESICE_NAMES = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

  return (
    <Stranka>
      <GradientHeader
        title="Log ST/Bonus"
        icon={History}
        loading={loading}
        akce={[
          { klic: "zpet", ikona: ArrowLeft, popis: "Zpět na klienty", onClick: () => navigate("/clients") },
          { klic: "st", ikona: Activity, popis: "Přidat spojený trénink (ST)", onClick: () => { setSubmitError(null); setModal("ST"); } },
          { klic: "bonus", ikona: Award, popis: "Přidat bonusový trénink", onClick: () => { setSubmitError(null); setModal("Bonus"); } },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading || refreshing },
        ]}
      />

      <div className="grid grid-cols-2 gap-3">
        <DlazdiceFiltr popisek="Celkem ST" hodnota={stats.totalST || 0} ikona={Activity} ton="znacka"
          info={<InfoTip text="Spojené tréninky letos (Reservio cron + manuální UI zápisy). Klik filtruje typ ST." />}
          zapnuto={tab === "st"} onClick={() => setTab(tab === "st" ? "all" : "st")} />
        <DlazdiceFiltr popisek="Celkem Bonus" hodnota={stats.totalBonus || 0} ikona={Award} ton="zlata"
          info={<InfoTip text="Bonusové tréninky letos (10 ST = 1 bonus). Klik filtruje typ Bonus." />}
          zapnuto={tab === "bonus"} onClick={() => setTab(tab === "bonus" ? "all" : "bonus")} />
      </div>

      <Card title="Záznamy" ikona={History} lista odznak={data ? `${filtered.length} z ${allEntries.length}` : undefined}>
        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative lg:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Hledat, i jen příjmení…" aria-label="Hledat" className="field pl-9" />
          </div>
          <select value={filterZadavatel} onChange={(e) => setFilterZadavatel(e.target.value)} aria-label="Filtrovat dle zadavatele"
            className={`field ${filterZadavatel !== "all" ? "border-brand font-semibold text-brand-deep" : ""}`}>
            <option value="all">Všichni zadavatelé</option>
            <option value="auto">Automatizace</option>
            <option value="manual">Manuální (všichni)</option>
            {zadavateleManual.length > 0 && (
              <optgroup label="Konkrétní osoba">
                {zadavateleManual.map((z) => <option key={z} value={z}>{z}</option>)}
              </optgroup>
            )}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} aria-label="Filtrovat dle měsíce"
              className={`field ${filterMonth !== "all" ? "border-brand font-semibold text-brand-deep" : ""}`}>
              <option value="all">Všechny měsíce</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                <option key={m} value={String(m)}>{MESICE_NAMES[m - 1]}</option>
              ))}
            </select>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} aria-label="Filtrovat dle roku"
              className={`field ${filterYear !== "all" ? "border-brand font-semibold text-brand-deep" : ""}`}>
              <option value="all">Všechny roky</option>
              {roky.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <button type="button" onClick={() => { setSubmitError(null); setModal("ST"); }} className="btn btn-primary btn-sm">
            <Plus className="h-4 w-4" /> Přidat ST
          </button>
          <button type="button" onClick={() => { setSubmitError(null); setModal("Bonus"); }} className="btn btn-secondary btn-sm">
            <Plus className="h-4 w-4" /> Přidat Bonus
          </button>
        </div>

        {error && <Hlaska ton="problem" title="Záznamy se nenačetly" akce={<AkceZnovu onClick={refetch} />}>{String(error)}</Hlaska>}

        {loading && allEntries.length === 0 ? (
          <SkeletonRows rows={6} />
        ) : !error && filtered.length === 0 ? (
          <EmptyState icon={History} title="Žádné záznamy" hint="Nic neodpovídá filtru." />
        ) : uzka ? (
          /* Telefon: ze sedmi sloupců byly vidět čtyři. Karta ukáže všechno. */
          <div className="space-y-2">
            {filtered.map((e, i) => {
              const isAuto = isAutoZadavatel(e.zadavatel);
              const popis = e.duvod || e.poznamka || (e.type === "ST" && e.trener ? `Trenér: ${e.trener}` : "");
              return (
                <div key={`${e.type}-${e.rowNum}-${i}-${e._tmp || ""}`} className={`polozka ${e._tmp ? "opacity-60" : ""}`}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[15px] font-bold leading-snug text-slate-900 demo-sensitive">{e.klient}</div>
                      <div className="mt-0.5 text-[12.5px] tabular-nums text-slate-500">{formatDate(e.datum)}</div>
                    </div>
                    <span className="shrink-0 text-[15px] font-black tabular-nums text-slate-900">{e.pocet}×</span>
                    {canDelete && (
                      <button type="button" onClick={() => setDeleteTarget(e)} title="Smazat záznam" aria-label="Smazat záznam"
                        className="btn-ikona shrink-0 border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {e.type === "ST"
                      ? <Stitek ton="znacka" velikost="mala" ikona={Activity}>ST</Stitek>
                      : <Stitek ton="zlata" velikost="mala" ikona={Award}>Bonus</Stitek>}
                    <Stitek ton="neutralni" velikost="mala" ikona={isAuto ? Bot : UserIcon}>{displayZadavatel(e.zadavatel)}</Stitek>
                  </div>
                  {popis && <div className="mt-1.5 text-[12.5px] leading-snug text-slate-600">{popis}</div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto sm:-mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-brand-50/60 text-xs uppercase tracking-wider text-slate-500">
                  <th className="whitespace-nowrap px-4 py-3 text-left font-semibold sm:px-5">Datum</th>
                  <th className="px-2 py-3 text-left font-semibold">Klient</th>
                  <th className="px-2 py-3 text-left font-semibold">Typ</th>
                  <th className="px-2 py-3 text-right font-semibold">Počet</th>
                  <th className="hidden px-2 py-3 text-left font-semibold md:table-cell">Zadavatel</th>
                  <th className="hidden px-2 py-3 text-left font-semibold lg:table-cell">Důvod / Poznámka</th>
                  {canDelete && <th className="w-14"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => {
                  const isAuto = isAutoZadavatel(e.zadavatel);
                  const zadavatelDisplay = displayZadavatel(e.zadavatel);
                  return (
                    <tr key={`${e.type}-${e.rowNum}-${i}-${e._tmp || ""}`} className={`border-b border-slate-100 hover:bg-brand-50/50 ${e._tmp ? "opacity-60" : ""}`}>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700 sm:px-5">{formatDate(e.datum)}</td>
                      <td className="px-2 py-3 font-medium text-slate-900 demo-sensitive">{e.klient}</td>
                      <td className="px-2 py-3">
                        {e.type === "ST"
                          ? <Stitek ton="znacka" velikost="mala" ikona={Activity}>ST</Stitek>
                          : <Stitek ton="zlata" velikost="mala" ikona={Award}>Bonus</Stitek>}
                      </td>
                      <td className="px-2 py-3 text-right font-semibold tabular-nums text-slate-900">{e.pocet}×</td>
                      <td className="hidden px-2 py-3 md:table-cell">
                        <span className={`inline-flex items-center gap-1 text-xs ${isAuto ? "text-slate-500" : "font-medium text-brand-deep"}`}>
                          {isAuto ? <Bot className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />} {zadavatelDisplay}
                        </span>
                      </td>
                      <td className="hidden max-w-md px-2 py-3 text-xs text-slate-600 lg:table-cell">
                        {e.duvod || e.poznamka || (e.type === "ST" && e.trener ? `Trenér: ${e.trener}` : "—")}
                      </td>
                      {canDelete && (
                        <td className="px-2 py-3">
                          <button type="button" onClick={() => setDeleteTarget(e)} title="Smazat záznam" aria-label="Smazat záznam"
                            className="btn-ikona border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modal && (
        <AddModal
          type={modal} onClose={() => setModal(null)} onSubmit={handleAdd}
          submitting={submitting} error={submitError} klientOptions={klientOptions} klientTrenerMap={klientTrenerMap}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title={`Smazat ${deleteTarget.type} záznam?`}
          message={
            <div className="space-y-1">
              <div><strong className="text-slate-900">{deleteTarget.klient}</strong></div>
              <div className="text-xs text-slate-500">
                {formatDate(deleteTarget.datum)}, {deleteTarget.pocet}× {deleteTarget.type}
                {deleteTarget.duvod && `, ${deleteTarget.duvod}`}
              </div>
              <div className="mt-2 flex items-start gap-1.5 text-xs text-rose-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>Tato akce je nevratná. Záznam se smaže z databáze a audit log zaznamená kdo to udělal.</span>
              </div>
            </div>
          }
          confirmLabel={deleting ? "Mažu…" : "Ano, smazat"}
          cancelLabel="Zrušit"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </Stranka>
  );
}
